import { ENPHASE_ACCESSORY_ITEMS } from '../constants/netsuite-items.constants';
import { LgcyEnphaseBomResults } from '../types/lgcy-bom.types';
import { NetsuiteItemResponse, NetsuiteItemState } from '../types/netsuite-items.types';

/** Input for computing the Enphase accessory NetSuite item sync */
export interface EnphaseNetsuiteSyncInput {
  /** NetSuite items available for the current client */
  catalogItems: NetsuiteItemResponse[];
  /** Currently selected NetSuite items */
  selectedItems: NetsuiteItemState[];
  /** Calculated Enphase BOM results (null when the project is not Enphase) */
  enphaseResults: LgcyEnphaseBomResults | null;
}

/** Actions required to bring the selected NetSuite items in sync */
export interface EnphaseNetsuiteSyncResult {
  itemsToAdd: NetsuiteItemState[];
  quantityUpdates: { id: string; quantity: number }[];
  idsToRemove: string[];
}

/** Options for mapping a NetSuite catalog item to selected-item state */
export interface NetsuiteItemStateOptions {
  quantity: number;
  autoAdded?: boolean;
}

/**
 * Maps a NetSuite catalog item to the Redux selected-item state.
 *
 * @param item - Catalog item from the client NetSuite database
 * @param options - Initial quantity and whether the item was auto-added
 * @returns Selected-item state ready for the createCR slice
 */
export const mapNetsuiteResponseToState = (
  item: NetsuiteItemResponse,
  options: NetsuiteItemStateOptions,
): NetsuiteItemState => ({
  id: item.id,
  external_id: item.external_id,
  name: item.name,
  display_name: item.display_name,
  item_category: item.item_category,
  manufacturer: item.manufacturer,
  quantity: options.quantity,
  enabled: true,
  auto_added: options.autoAdded ?? false,
});

/**
 * Computes the actions needed to keep the Enphase accessory NetSuite items
 * (Q Cable parts) in sync with the calculated LGCY Enphase BOM quantities.
 *
 * Rules per accessory:
 * - Calculated quantity > 0 and item not selected → add it (marked auto-added)
 * - Calculated quantity > 0 and item selected with a different quantity → update it,
 *   unless the user manually edited the quantity (quantity_edited) — edits are kept
 * - Calculated quantity is 0 (or project is not Enphase) → remove it, but only
 *   if it was auto-added; manually added items are never removed
 *
 * @param input - Catalog, current selection, and calculated Enphase results
 * @returns Add/update/remove actions to dispatch
 */
export const computeEnphaseNetsuiteSync = (
  input: EnphaseNetsuiteSyncInput,
): EnphaseNetsuiteSyncResult => {
  const { catalogItems, selectedItems, enphaseResults } = input;
  const result: EnphaseNetsuiteSyncResult = { itemsToAdd: [], quantityUpdates: [], idsToRemove: [] };

  for (const accessory of ENPHASE_ACCESSORY_ITEMS) {
    const selected = selectedItems.find(item => item.external_id === accessory.externalId);
    const quantity = enphaseResults?.[accessory.quantityField] ?? 0;

    if (quantity <= 0) {
      if (selected?.auto_added) {
        result.idsToRemove.push(selected.id);
      }
      continue;
    }

    if (!selected) {
      const catalogItem = catalogItems.find(item => item.external_id === accessory.externalId);
      if (catalogItem) {
        result.itemsToAdd.push(mapNetsuiteResponseToState(catalogItem, { quantity, autoAdded: true }));
      }
      continue;
    }

    if (selected.quantity !== quantity && !selected.quantity_edited) {
      result.quantityUpdates.push({ id: selected.id, quantity });
    }
  }

  return result;
};

/**
 * Finds Enphase accessory items that are missing from the client NetSuite catalog,
 * so the UI can warn instead of silently skipping them.
 *
 * @param catalogItems - NetSuite items available for the current client
 * @returns External IDs of accessories not present in the catalog
 */
export const findMissingEnphaseAccessories = (catalogItems: NetsuiteItemResponse[]): string[] => {
  const catalogExternalIds = new Set(catalogItems.map(item => item.external_id));
  return ENPHASE_ACCESSORY_ITEMS
    .filter(accessory => !catalogExternalIds.has(accessory.externalId))
    .map(accessory => accessory.externalId);
};
